import { FALLBACK_BAND_COLORS } from '../config'

/**
 * Color helpers for accessible author bands and AI-suggested highlight colors.
 */
export function normalizeHexColor(value) {
  const clean = String(value || '').trim()
  const shortHex = clean.match(/^#?([0-9a-fA-F]{3})$/)
  if (shortHex) {
    return `#${shortHex[1]
      .split('')
      .map(char => char + char)
      .join('')
      .toLowerCase()}`
  }
  const longHex = clean.match(/^#?([0-9a-fA-F]{6})$/)
  return longHex ? `#${longHex[1].toLowerCase()}` : ''
}

export function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex)
  if (!normalized) return null
  const value = normalized.slice(1)
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  }
}

export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  const channels = [rgb.r, rgb.g, rgb.b].map(value => {
    const channel = value / 255
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function contrastRatio(colorA, colorB) {
  const light = Math.max(relativeLuminance(colorA), relativeLuminance(colorB))
  const dark = Math.min(relativeLuminance(colorA), relativeLuminance(colorB))
  return (light + 0.05) / (dark + 0.05)
}

export function colorDistance(colorA, colorB) {
  const rgbA = hexToRgb(colorA)
  const rgbB = hexToRgb(colorB)
  if (!rgbA || !rgbB) return 0
  return Math.sqrt(
    Math.pow(rgbA.r - rgbB.r, 2) +
      Math.pow(rgbA.g - rgbB.g, 2) +
      Math.pow(rgbA.b - rgbB.b, 2)
  )
}

export function isAccessibleBandColor(color, characterColors = []) {
  const normalized = normalizeHexColor(color)
  if (!normalized) return false
  const distinctFromCharacters = characterColors.every(
    characterColor => colorDistance(normalized, characterColor) >= 80
  )
  return contrastRatio(normalized, '#ffffff') >= 4.5 && distinctFromCharacters
}

export function getFallbackBandColor(label = '') {
  const hash = String(label)
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return FALLBACK_BAND_COLORS[hash % FALLBACK_BAND_COLORS.length]
}
